/**
 * Use this to allow a particular function to be called a maximim of 1 call per N milliseconds. Each call is either
 * - queued up to be called once N milliseconds have passed, discarding the previously queued call
 * - executed immediately
 *
 * By default N is 1000, but you can change that via the constructor.
 */
export class Debounce<T> {
  private static readonly DEFAULT_TIME_BETWEEN_CALLS = 1000

  private readonly action: (arg: T) => void
  private readonly debounceMs: number
  private lastCallTime = 0 // zero so that the first call is immediately evaluated
  private queuedExecution: NodeJS.Timeout | null = null

  constructor(action: (arg: T) => void, debounceMs: number = Debounce.DEFAULT_TIME_BETWEEN_CALLS) {
    this.action = action
    this.debounceMs = debounceMs
  }

  public exec = (arg: T): void => {
    // cancel any previously queued up calls
    this.clearQueuedExecution()

    const now = new Date().getTime()
    const timeBetweenNowAndLastCall = now - this.lastCallTime
    const timeUntilNextAllowedTime = this.debounceMs - timeBetweenNowAndLastCall

    // if more than `debounceMs` milliseconds have passed since the last call, go ahead and just do the call immediately
    if (timeUntilNextAllowedTime <= 0) {
      this.lastCallTime = now
      this.action(arg)
    } else {
      // otherwise, optimistically do the call once `debounceMs` HAS passed.
      this.queuedExecution = setTimeout(() => this.action(arg), timeUntilNextAllowedTime)
    }
  }

  private clearQueuedExecution() {
    if (this.queuedExecution != null) {
      clearTimeout(this.queuedExecution)
      this.queuedExecution = null
    }
  }
}
